import { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../api/apiClient";
import { useT } from "../../i18n/LanguageProvider";
import { useAuth } from "../../state/AuthContext";
import {
  AccountShell,
  errorStatus,
  useAccountLanguage,
  useLinkPreview,
  useLinkToken,
} from "./accountLink";

/**
 * G-136 — `/account/confirm-email#token=…`: a verify-your-address link, or a
 * new address confirming itself.
 *
 * Confirmation takes a click, never the page load: some mail scanners run a
 * page's scripts, and an address change must not happen because a robot
 * opened the email. A completed change ends every session, so the page then
 * sends the person to sign in with the new address.
 */
export function AccountConfirmEmailPage() {
  const token = useLinkToken();
  const link = useLinkPreview(token, ["EMAIL_VERIFY", "EMAIL_CHANGE"]);
  useAccountLanguage(link.kind === "ready" ? link.preview.language : null);
  const { t } = useT();
  const { isAuthenticated, user, refreshUser, endCrmSessionLocally } = useAuth();
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (link.kind === "loading") {
    return (
      <AccountShell>
        <p className="page-subtitle account-center">{t("account.loading")}</p>
      </AccountShell>
    );
  }

  if (link.kind === "unusable") {
    return (
      <AccountShell>
        <h1 className="page-title account-center">{t("account.linkUnusableTitle")}</h1>
        <p className="account-center">{t("account.linkUnusable")}</p>
        <Link className="button" to="/login">
          {t("account.goToSignIn")}
        </Link>
      </AccountShell>
    );
  }

  const { preview } = link;
  const isChange = preview.purpose === "EMAIL_CHANGE";

  if (done) {
    return (
      <AccountShell>
        <h1 className="page-title account-center">
          {isChange ? t("account.changedTitle") : t("account.verifiedTitle")}
        </h1>
        <p className="account-center">
          {isChange ? t("account.changed", { email: preview.email }) : t("account.verified")}
        </p>
        {/* A verified address changes nothing about the session; a change ended it. */}
        <Link className="button" to={!isChange && isAuthenticated ? "/app" : "/login"}>
          {!isChange && isAuthenticated ? t("account.continue") : t("account.goToSignIn")}
        </Link>
      </AccountShell>
    );
  }

  const confirmAddress = async () => {
    setError(null);
    setBusy(true);
    try {
      await api.confirmAccountEmail(token);
      const signedInAsThem = user?.id === preview.userId;
      if (isChange && signedInAsThem) {
        // The change ended every session of this person on the server; end
        // this one here too, straight to sign-in with the new address.
        endCrmSessionLocally(t("account.changed", { email: preview.email }));
        return;
      }
      // A confirmed address lifts the reminder banner for a signed-in person.
      if (!isChange && signedInAsThem) await refreshUser().catch(() => undefined);
      setDone(true);
    } catch (err: unknown) {
      const status = errorStatus(err);
      setError(
        status === 410
          ? t("account.linkUnusable")
          : status === 409
            ? t("account.addressTaken")
            : t("account.genericError"),
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <AccountShell>
      <h1 className="page-title account-center">
        {isChange ? t("account.confirmChangeTitle") : t("account.confirmVerifyTitle")}
      </h1>
      <p className="account-center">
        {isChange
          ? t("account.confirmChangeLead", { email: preview.email })
          : t("account.confirmVerifyLead", { email: preview.email })}
      </p>
      {error ? <div className="alert error">{error}</div> : null}
      <div className="button-row">
        <button type="button" className="button" disabled={busy} onClick={() => void confirmAddress()}>
          {busy ? t("account.confirming") : t("account.confirmSubmit")}
        </button>
      </div>
    </AccountShell>
  );
}
